import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ProjetoStatus } from '@/types';
import { ProjetoStatusBadge } from './ProjetoStatusBadge';
import { ArrowRight, XCircle } from 'lucide-react';

interface Props {
  // Estrutural em vez de `Projeto` para servir também ao ProjetoT, cujo status mora em
  // `status_projeto` — a tela passa `{ titulo, status: status_projeto }`.
  projeto: { titulo: string; status: ProjetoStatus };
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStatusChange: (novoStatus: ProjetoStatus) => Promise<void>;
  // Usuário comum dono do projeto: só pode enviar pra aprovação (o resto do fluxo é do admin).
  somenteEnviarParaAprovacao?: boolean;
}

const proximoStatus: Partial<Record<ProjetoStatus, ProjetoStatus>> = {
  em_edicao: 'ag_aprovacao',
  ag_aprovacao: 'em_andamento',
  em_andamento: 'ag_validacao',
  ag_validacao: 'finalizado',
};

const statusFinal: ProjetoStatus[] = ['finalizado', 'reprovado'];

export function ProjetoStatusFlow({ projeto, open, onOpenChange, onStatusChange, somenteEnviarParaAprovacao = false }: Props) {
  const [loading, setLoading] = useState(false);

  const proximo = somenteEnviarParaAprovacao && projeto.status !== 'em_edicao' ? undefined : proximoStatus[projeto.status];
  const podeReprovar = !somenteEnviarParaAprovacao && !statusFinal.includes(projeto.status);

  const handleAvancar = async () => {
    if (!proximo) return;
    setLoading(true);
    try {
      await onStatusChange(proximo);
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  const handleReprovar = async () => {
    setLoading(true);
    try {
      await onStatusChange('reprovado');
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Fluxo de Aprovação</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <p className="text-sm text-muted-foreground mb-1">Projeto</p>
            <p className="font-medium">{projeto.titulo}</p>
          </div>

          <div>
            <p className="text-sm text-muted-foreground mb-1">Status atual</p>
            <ProjetoStatusBadge status={projeto.status} />
          </div>

          {proximo && (
            <div className="flex items-center gap-2">
              <ProjetoStatusBadge status={projeto.status} />
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
              <ProjetoStatusBadge status={proximo} />
            </div>
          )}

          {!proximo && !podeReprovar && (
            <p className="text-sm text-muted-foreground">
              {somenteEnviarParaAprovacao && !statusFinal.includes(projeto.status)
                ? 'As próximas etapas (aprovar, reprovar, validar) são feitas por um administrador.'
                : 'Este projeto não possui mais transições de status disponíveis.'}
            </p>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancelar
          </Button>
          {podeReprovar && (
            <Button variant="destructive" onClick={handleReprovar} disabled={loading} className="gap-2">
              <XCircle className="h-4 w-4" />
              Reprovar
            </Button>
          )}
          {proximo && (
            <Button onClick={handleAvancar} disabled={loading} className="gap-2">
              <ArrowRight className="h-4 w-4" />
              Avançar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
